import { useState, useEffect, useRef } from "react";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { CLOUD_RUN_URL } from "@/services/shared";
import { supabase } from "@/services/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
// ScrollArea removed — plain overflow-y-auto divs work more reliably for max-h limits
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertTriangle,
  Upload,
  Play,
  XCircle,
  Loader2,
  FileText,
  Database,
  FlaskConical,
  Camera,
  History,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Eye,
  Download,
} from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ValidationResult {
  matched_files: { file_name: string; project_file_id: string; csv_name: string }[];
  unmatched_files: string[];
  total_project_files: number;
  match_count: number;
  unmatch_count: number;
}

interface DatasetExample {
  id: string;
  project_id: string;
  title: string;
  prompt: string;
  procurement_form: string;
  expected_files: { file_name: string; project_file_id: string }[] | null;
  has_ground_truth: boolean;
  expected_file_count: number;
}

interface DatasetInfo {
  name: string;
  id: string;
  example_count: number;
  created_at: string;
}

interface SnapshotPreview {
  title: string;
  prompt: string;
  procurement_form: string;
  files: { file_name: string; project_file_id: string }[];
  file_count: number;
}

interface SelectedFile {
  project_file_id: string;
  file_name: string;
  reason: string;
  confidence: number;
}

interface Comparison {
  match_type: "match" | "missing" | "unexpected";
  file_name: string;
  project_file_id?: string;
  confidence?: number;
  reason?: string;
}

interface EvalResult {
  title: string;
  project_id: string;
  selected_files: SelectedFile[];
  summary: string;
  latency_seconds: number;
  step_count: number;
  precision: number | null;
  recall: number | null;
  f1: number | null;
  tp: number;
  fp: number;
  fn: number;
  missing_files: string[] | null;
  unexpected_files: string[] | null;
  comparisons: Comparison[] | null;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
}

interface BatchRunItem {
  title: string;
  example_id: string;
  runIndex: number;
  status: "queued" | "running" | "completed" | "failed";
  result?: EvalResult;
  error?: string;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildPrUrlTemplate(prodUrl: string): string {
  try {
    const parsed = new URL(prodUrl);
    return `${parsed.protocol}//pr-{N}---${parsed.host}`;
  } catch {
    return "";
  }
}

function buildPrOriginPattern(template: string): RegExp | null {
  if (!template) return null;
  const placeholder = "__PR_NUMBER__";
  try {
    const origin = new URL(template.replace("{N}", placeholder)).origin;
    return new RegExp(`^${escapeRegex(origin).replace(placeholder, "\\d+")}$`);
  } catch {
    return null;
  }
}

function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

const DEFAULT_GROUPING_EVAL_PROD_URL = "https://byggve-api-245372926864.europe-north2.run.app";
const PROD_URL = import.meta.env.VITE_GROUPING_EVAL_PROD_URL || DEFAULT_GROUPING_EVAL_PROD_URL;
const PR_URL_TEMPLATE =
  import.meta.env.VITE_GROUPING_EVAL_PR_URL_TEMPLATE || buildPrUrlTemplate(PROD_URL);
const TRUSTED_PR_ORIGIN_PATTERN = buildPrOriginPattern(PR_URL_TEMPLATE);

interface ExperimentSummary {
  id: string;
  name: string;
  dataset_name: string;
  dataset_id: string;
  run_count: number;
  created_at: string | null;
  last_run_at: string | null;
  scores: Record<string, number | null>;
  metadata: Record<string, any>;
}

interface ExperimentExampleResult {
  example_id: string;
  title: string;
  project_id: string;
  scores: Record<string, number | null>;
  selected_file_count: number;
  selected_files: SelectedFile[];
  missing_files: string[] | null;
  unexpected_files: string[] | null;
  comparisons: Comparison[] | null;
  summary: string;
  latency_seconds: number | null;
  run_id: string;
  run_start_time: string | null;
}

interface ExperimentDetail {
  experiment_name: string;
  run_count: number;
  aggregate_scores: Record<string, number | null>;
  examples: ExperimentExampleResult[];
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const LANGSMITH_ORG_ID = import.meta.env.VITE_LANGSMITH_ORG_ID || "";
const LANGSMITH_BASE = LANGSMITH_ORG_ID
  ? `https://eu.smith.langchain.com/o/${LANGSMITH_ORG_ID}`
  : "";
const ADMIN_EVAL_GROUPING_PREFIX = "/api/admin/eval/grouping";

async function getAuthToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || "";
}

function isTrustedBackend(url: string): boolean {
  if (!url) return false;
  try {
    const { origin } = new URL(url);
    if (CLOUD_RUN_URL && origin === new URL(CLOUD_RUN_URL).origin) return true;
    if (origin === new URL(PROD_URL).origin) return true;
    return TRUSTED_PR_ORIGIN_PATTERN?.test(origin) ?? false;
  } catch {
    return false;
  }
}

function toAdminEvalPath(path: string): string {
  if (path.startsWith("/api/eval/grouping")) {
    return path.replace("/api/eval/grouping", ADMIN_EVAL_GROUPING_PREFIX);
  }
  return path;
}

async function evalFetch(path: string, options: RequestInit = {}, baseUrl?: string) {
  const base = baseUrl || CLOUD_RUN_URL;
  if (!base) {
    throw new Error("VITE_CLOUD_RUN_URL is not configured — cannot reach eval backend");
  }
  const resolvedPath = toAdminEvalPath(path);
  if (resolvedPath.startsWith(ADMIN_EVAL_GROUPING_PREFIX) && !isTrustedBackend(base)) {
    throw new Error("Custom backends must use a trusted Brickanta backend host");
  }
  const token = await getAuthToken();
  // Only send auth token to trusted Brickanta backends — never to custom URLs
  const includeAuth = isTrustedBackend(base);
  const res = await fetch(`${base}${resolvedPath}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(includeAuth ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Shared components
// ---------------------------------------------------------------------------

function PreviewButton({ projectFileId }: { projectFileId?: string }) {
  const [loading, setLoading] = useState(false);

  if (!projectFileId) return null;

  const handlePreview = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      // Intentionally targets the default (prod) backend regardless of the
      // selected eval backend — signed URLs come from prod Supabase Storage
      // where the actual project files live.
      const { url } = await evalFetch(
        `/api/eval/grouping/files/signed-url?project_file_id=${projectFileId}`,
      );
      window.open(url, "_blank");
    } catch (err: any) {
      toast.error(`Preview failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0"
      onClick={handlePreview}
      disabled={loading}
      title="Preview file"
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
    </Button>
  );
}

function ComparisonTable({
  comparisons,
  exportName,
}: {
  comparisons: Comparison[];
  exportName?: string;
}) {
  const matches = comparisons.filter((c) => c.match_type === "match");
  const missing = comparisons.filter((c) => c.match_type === "missing");
  const unexpected = comparisons.filter((c) => c.match_type === "unexpected");
  const sorted = [...missing, ...unexpected, ...matches];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="text-green-700">{matches.length} match</span>
        <span className="text-red-700">{missing.length} missing</span>
        <span className="text-orange-700">{unexpected.length} unexpected</span>
        <span>{comparisons.length} total</span>
        {exportName && (
          <button
            className="ml-auto hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              exportComparisons(exportName, comparisons);
            }}
            title="Export as CSV"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[90px]">Status</TableHead>
            <TableHead className="w-[280px]">File Name</TableHead>
            <TableHead className="w-[70px]">Conf.</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="w-[40px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((c, i) => (
            <TableRow
              key={`${c.file_name}-${i}`}
              className={
                c.match_type === "missing"
                  ? "bg-destructive/5"
                  : c.match_type === "unexpected"
                    ? "bg-warning/5"
                    : ""
              }
            >
              <TableCell>
                <Badge
                  variant={
                    c.match_type === "match"
                      ? "success"
                      : c.match_type === "missing"
                        ? "destructive"
                        : "warning"
                  }
                  className="text-xs capitalize"
                >
                  {c.match_type}
                </Badge>
              </TableCell>
              <TableCell className="font-mono text-xs break-all">{c.file_name}</TableCell>
              <TableCell className="text-xs">
                {isDefined(c.confidence) ? `${Math.round(c.confidence * 100)}%` : "\u2014"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground whitespace-pre-wrap">
                {c.reason || "\u2014"}
              </TableCell>
              <TableCell>
                <PreviewButton projectFileId={c.project_file_id} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CreateDatasetTab() {
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [procurementForm, setProcurementForm] = useState("");
  const [datasetName, setDatasetName] = useState("grouping-eval-v3");
  const [csvContent, setCsvContent] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCsvConfirm, setShowCsvConfirm] = useState(false);

  // Snapshot fields
  const [snapshotPackageId, setSnapshotPackageId] = useState("");
  const [snapshotProjectId, setSnapshotProjectId] = useState("");
  const [snapshotDatasetName, setSnapshotDatasetName] = useState("grouping-eval-v3");
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotPreview, setSnapshotPreview] = useState<SnapshotPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setValidation(null);
    setShowCsvConfirm(false);
    const reader = new FileReader();
    reader.onload = () => setCsvContent(reader.result as string);
    reader.readAsText(file);
  };

  const handleValidate = async () => {
    if (!projectId || !csvContent) {
      toast.error("Project ID and CSV are required for validation");
      return;
    }
    setValidating(true);
    setValidation(null);
    setShowCsvConfirm(false);
    try {
      const result = await evalFetch("/api/eval/grouping/validate", {
        method: "POST",
        body: JSON.stringify({ project_id: projectId, csv_content: csvContent }),
      });
      setValidation(result);
      toast.success(
        `Matched ${result.match_count}/${result.match_count + result.unmatch_count} files`,
      );
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setValidating(false);
    }
  };

  const handleReviewBeforeCreate = () => {
    if (!projectId || !title || !datasetName) {
      toast.error("Dataset name, project ID, and title are required");
      return;
    }
    setShowCsvConfirm(true);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const body: Record<string, any> = {
        dataset_name: datasetName,
        project_id: projectId,
        title,
        prompt,
        procurement_form: procurementForm,
      };
      if (validation?.matched_files?.length) {
        body.expected_files = validation.matched_files.map((f) => ({
          file_name: f.file_name,
          project_file_id: f.project_file_id,
        }));
      }
      await evalFetch("/api/eval/grouping/datasets", {
        method: "POST",
        body: JSON.stringify(body),
      });
      toast.success("Dataset example created");
      setShowCsvConfirm(false);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleSnapshotPreview = async () => {
    if (!snapshotProjectId || !snapshotPackageId) {
      toast.error("Project ID and Package ID are required");
      return;
    }
    setLoadingPreview(true);
    setSnapshotPreview(null);
    try {
      const result = await evalFetch("/api/eval/grouping/datasets/snapshot/preview", {
        method: "POST",
        body: JSON.stringify({
          project_id: snapshotProjectId,
          package_id: snapshotPackageId,
        }),
      });
      setSnapshotPreview(result);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSnapshotConfirm = async () => {
    if (!snapshotProjectId || !snapshotPackageId || !snapshotDatasetName) {
      toast.error("All snapshot fields are required");
      return;
    }
    setSnapshotting(true);
    try {
      await evalFetch("/api/eval/grouping/datasets/snapshot", {
        method: "POST",
        body: JSON.stringify({
          dataset_name: snapshotDatasetName,
          project_id: snapshotProjectId,
          package_id: snapshotPackageId,
        }),
      });
      toast.success("Package snapshotted as dataset example");
      setSnapshotPreview(null);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSnapshotting(false);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Left: Create from CSV */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-4 w-4" />
            Create from CSV
          </CardTitle>
          <CardDescription>
            Upload a CSV with FILE_NAME column as ground truth, or create without ground truth.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Dataset Name</Label>
            <Input
              value={datasetName}
              onChange={(e) => setDatasetName(e.target.value)}
              placeholder="grouping-eval-v3"
            />
          </div>
          <div className="space-y-2">
            <Label>Project ID</Label>
            <Input
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="Supabase project UUID"
              className="font-mono text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Package Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Kakel och klinkerarbeten"
              />
            </div>
            <div className="space-y-2">
              <Label>Procurement Form</Label>
              <Input
                value={procurementForm}
                onChange={(e) => setProcurementForm(e.target.value)}
                placeholder="AB, ABM07..."
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Prompt</Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Hus A, plan 4-12"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Ground Truth CSV (optional)</Label>
            <div className="flex items-center gap-2">
              <label className="flex-1">
                <div className="flex items-center gap-2 border border-dashed rounded-md p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {csvFileName || "Choose CSV file..."}
                  </span>
                </div>
                <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} />
              </label>
              {csvContent && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleValidate}
                  disabled={validating || !projectId}
                >
                  {validating ? <Loader2 className="h-3 w-3 animate-spin" /> : "Validate"}
                </Button>
              )}
            </div>
          </div>

          {/* Validation results */}
          {validation && (
            <div className="rounded-md border p-3 space-y-2 text-sm">
              <div className="flex items-center gap-4">
                <Badge variant={validation.unmatch_count === 0 ? "success" : "warning"}>
                  {validation.match_count} matched
                </Badge>
                {validation.unmatch_count > 0 && (
                  <Badge variant="destructive">{validation.unmatch_count} not found</Badge>
                )}
                <span className="text-muted-foreground text-xs">
                  of {validation.total_project_files} project files
                </span>
              </div>
              {validation.unmatched_files.length > 0 && (
                <div className="text-xs text-destructive space-y-1">
                  <p className="font-medium">Unmatched files (will be ignored):</p>
                  {validation.unmatched_files.map((f) => (
                    <p key={f} className="pl-2 flex items-center gap-1">
                      <XCircle className="h-3 w-3" /> {f}
                    </p>
                  ))}
                </div>
              )}
              {validation.matched_files.length > 0 && (
                <div className="overflow-y-auto max-h-[150px]">
                  <div className="text-xs space-y-0.5">
                    <p className="font-medium text-muted-foreground">
                      Matched files ({validation.match_count}):
                    </p>
                    {validation.matched_files.map((f) => (
                      <p key={f.project_file_id} className="pl-2 text-muted-foreground">
                        {f.file_name}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Review / Confirm */}
          {!showCsvConfirm ? (
            <Button
              className="w-full"
              onClick={handleReviewBeforeCreate}
              disabled={!projectId || !title}
            >
              Review before creating
            </Button>
          ) : (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-3">
              <p className="text-sm font-semibold">Confirm dataset example</p>
              <div className="text-xs space-y-1">
                <p>
                  <span className="text-muted-foreground">Dataset:</span> {datasetName}
                </p>
                <p>
                  <span className="text-muted-foreground">Title:</span> {title}
                </p>
                {prompt && (
                  <p>
                    <span className="text-muted-foreground">Prompt:</span> {prompt}
                  </p>
                )}
                {procurementForm && (
                  <p>
                    <span className="text-muted-foreground">Form:</span> {procurementForm}
                  </p>
                )}
                <p>
                  <span className="text-muted-foreground">Project:</span>{" "}
                  <span className="font-mono">{projectId}</span>
                </p>
                {validation?.matched_files?.length ? (
                  <p>
                    <span className="text-muted-foreground">Ground truth:</span>{" "}
                    {validation.match_count} files
                  </p>
                ) : (
                  <p className="text-warning">
                    No ground truth — example will have no expected files
                  </p>
                )}
                {validation && validation.unmatch_count > 0 && (
                  <p className="text-destructive">
                    {validation.unmatch_count} CSV files will be ignored (not found in project)
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" onClick={handleCreate} disabled={creating}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Confirm & Create
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCsvConfirm(false)}
                  disabled={creating}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right: Snapshot existing package */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera className="h-4 w-4" />
            Snapshot Package
          </CardTitle>
          <CardDescription>Capture an existing production package as ground truth.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Dataset Name</Label>
            <Input
              value={snapshotDatasetName}
              onChange={(e) => setSnapshotDatasetName(e.target.value)}
              placeholder="grouping-eval-v3"
            />
          </div>
          <div className="space-y-2">
            <Label>Project ID</Label>
            <Input
              value={snapshotProjectId}
              onChange={(e) => {
                setSnapshotProjectId(e.target.value);
                setSnapshotPreview(null);
              }}
              placeholder="Supabase project UUID"
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-2">
            <Label>Package ID</Label>
            <Input
              value={snapshotPackageId}
              onChange={(e) => {
                setSnapshotPackageId(e.target.value);
                setSnapshotPreview(null);
              }}
              placeholder="Supabase package UUID"
              className="font-mono text-xs"
            />
          </div>

          {/* Preview / Confirm flow */}
          {!snapshotPreview ? (
            <Button
              className="w-full"
              variant="outline"
              onClick={handleSnapshotPreview}
              disabled={loadingPreview || !snapshotProjectId || !snapshotPackageId}
            >
              {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Preview Package
            </Button>
          ) : (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-3">
              <p className="text-sm font-semibold">Package preview</p>
              <div className="text-xs space-y-1">
                <p>
                  <span className="text-muted-foreground">Title:</span>{" "}
                  {snapshotPreview.title || <span className="italic text-warning">empty</span>}
                </p>
                {snapshotPreview.prompt && (
                  <p>
                    <span className="text-muted-foreground">Prompt:</span> {snapshotPreview.prompt}
                  </p>
                )}
                {snapshotPreview.procurement_form && (
                  <p>
                    <span className="text-muted-foreground">Form:</span>{" "}
                    {snapshotPreview.procurement_form}
                  </p>
                )}
                <p>
                  <span className="text-muted-foreground">Files:</span> {snapshotPreview.file_count}{" "}
                  files as ground truth
                </p>
              </div>
              <div className="overflow-y-auto max-h-[200px]">
                <div className="text-xs space-y-0.5">
                  {snapshotPreview.files.map((f) => (
                    <p key={f.project_file_id} className="pl-2 text-muted-foreground">
                      {f.file_name}
                    </p>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={handleSnapshotConfirm}
                  disabled={snapshotting || !snapshotDatasetName}
                >
                  {snapshotting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Confirm & Snapshot
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSnapshotPreview(null)}
                  disabled={snapshotting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function exportDatasetCsv(datasetName: string, example: DatasetExample) {
  // Exports a single example's expected files as a CSV with FILE_NAME column,
  // matching the format that Create Dataset's CSV import expects.
  const rows: string[][] = [["FILE_NAME"]];
  for (const f of example.expected_files ?? []) {
    rows.push([f.file_name]);
  }
  downloadCsv(`${datasetName}_${example.title.replace(/\s+/g, "-")}.csv`, rows);
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function exportSelectedFiles(filename: string, files: SelectedFile[]) {
  const rows: string[][] = [["FILE_NAME", "CONFIDENCE", "REASON"]];
  for (const f of files) {
    rows.push([f.file_name, String(Math.round(f.confidence * 100)) + "%", f.reason || ""]);
  }
  downloadCsv(filename, rows);
}

function exportComparisons(filename: string, comparisons: Comparison[]) {
  const rows: string[][] = [["STATUS", "FILE_NAME", "CONFIDENCE", "REASON"]];
  for (const c of comparisons) {
    rows.push([
      c.match_type,
      c.file_name,
      isDefined(c.confidence) ? Math.round(c.confidence * 100) + "%" : "",
      c.reason || "",
    ]);
  }
  downloadCsv(filename, rows);
}

function exportBatchResults(filename: string, items: BatchRunItem[]) {
  const rows: string[][] = [
    ["EXAMPLE", "STATUS", "PRECISION", "RECALL", "F1", "FILES", "LATENCY_S", "SELECTED_FILES"],
  ];
  for (const item of items) {
    const r = item.result;
    rows.push([
      item.title,
      item.status,
      isDefined(r?.precision) ? (r.precision * 100).toFixed(1) + "%" : "",
      isDefined(r?.recall) ? (r.recall * 100).toFixed(1) + "%" : "",
      isDefined(r?.f1) ? (r.f1 * 100).toFixed(1) + "%" : "",
      r ? String(r.selected_files.length) : "",
      r ? String(r.latency_seconds) : "",
      r ? r.selected_files.map((f) => f.file_name).join("; ") : "",
    ]);
  }
  downloadCsv(filename, rows);
}

function exportExperimentResults(filename: string, examples: ExperimentExampleResult[]) {
  const rows: string[][] = [
    ["EXAMPLE", "PRECISION", "RECALL", "F1", "FILES", "LATENCY_S", "SELECTED_FILES"],
  ];
  for (const ex of examples) {
    rows.push([
      ex.title,
      isDefined(ex.scores.precision) ? (ex.scores.precision * 100).toFixed(1) + "%" : "",
      isDefined(ex.scores.recall) ? (ex.scores.recall * 100).toFixed(1) + "%" : "",
      isDefined(ex.scores.f1) ? (ex.scores.f1 * 100).toFixed(1) + "%" : "",
      String(ex.selected_file_count),
      isDefined(ex.latency_seconds) ? ex.latency_seconds.toFixed(1) : "",
      ex.selected_files.map((f) => f.file_name).join("; "),
    ]);
  }
  downloadCsv(filename, rows);
}

function DatasetsTab({ datasets, loading }: { datasets: DatasetInfo[]; loading: boolean }) {
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [examples, setExamples] = useState<DatasetExample[]>([]);
  const [loadingExamples, setLoadingExamples] = useState(false);

  // Experiments for selected dataset
  const [experiments, setExperiments] = useState<ExperimentSummary[]>([]);
  const [loadingExperiments, setLoadingExperiments] = useState(false);
  const [expandedExpId, setExpandedExpId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ExperimentDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const selectDataset = async (ds: DatasetInfo) => {
    setSelectedDataset(ds.name);
    setSelectedDatasetId(ds.id);
    setExamples([]);
    setExperiments([]);
    setExpandedExpId(null);
    setDetail(null);

    // Fetch examples and experiments in parallel
    setLoadingExamples(true);
    setLoadingExperiments(true);

    const examplesP = evalFetch(
      `/api/eval/grouping/datasets/${encodeURIComponent(ds.name)}/examples`,
    )
      .then((result) => setExamples(result))
      .catch((err: any) => toast.error(err.message))
      .finally(() => setLoadingExamples(false));

    const experimentsP = evalFetch(
      `/api/eval/grouping/experiments?dataset_name=${encodeURIComponent(ds.name)}`,
    )
      .then((result: ExperimentSummary[]) => {
        setExperiments(result);
      })
      .catch((err: any) => toast.error(err.message))
      .finally(() => setLoadingExperiments(false));

    await Promise.all([examplesP, experimentsP]);
  };

  const toggleExperiment = async (exp: ExperimentSummary) => {
    if (expandedExpId === exp.id) {
      setExpandedExpId(null);
      setDetail(null);
      return;
    }
    setExpandedExpId(exp.id);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const result = await evalFetch(
        `/api/eval/grouping/experiments/${encodeURIComponent(exp.name)}/results`,
      );
      setDetail(result);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  const pct = (v: number | null | undefined) =>
    isDefined(v) ? `${(v * 100).toFixed(1)}%` : "\u2014";

  const formatDate = (d: string | null) => {
    if (!d) return "\u2014";
    try {
      return new Date(d).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return d;
    }
  };

  return (
    <div className="space-y-4">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading datasets...
        </div>
      )}

      {datasets.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {datasets.map((ds) => (
            <Card
              key={ds.id}
              className={`cursor-pointer transition-colors hover:border-primary/50 ${selectedDataset === ds.name ? "border-primary" : ""}`}
              onClick={() => selectDataset(ds)}
            >
              <CardContent className="p-4">
                <p className="font-medium text-sm">{ds.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{ds.example_count} examples</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedDataset && (
        <>
          {/* Examples */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{selectedDataset}</CardTitle>
                  <CardDescription>
                    {loadingExamples ? "Loading..." : `${examples.length} examples`}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!loadingExamples && examples.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Project ID</TableHead>
                      <TableHead>Prompt</TableHead>
                      <TableHead>Ground Truth</TableHead>
                      <TableHead className="text-right">Expected Files</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {examples.map((ex) => (
                      <TableRow key={ex.id}>
                        <TableCell className="font-medium">{ex.title}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {ex.project_id?.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs">
                          {ex.prompt}
                        </TableCell>
                        <TableCell>
                          {ex.has_ground_truth ? (
                            <Badge variant="success">Yes</Badge>
                          ) : (
                            <Badge variant="secondary">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{ex.expected_file_count}</TableCell>
                        <TableCell>
                          {ex.has_ground_truth && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => exportDatasetCsv(selectedDataset, ex)}
                              title="Export expected files as CSV"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Experiments (run history) for this dataset */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4" />
                Run History
              </CardTitle>
              <CardDescription>
                {loadingExperiments
                  ? "Loading..."
                  : experiments.length > 0
                    ? `${experiments.length} experiment${experiments.length !== 1 ? "s" : ""}`
                    : "No experiments yet for this dataset"}
              </CardDescription>
            </CardHeader>
            {experiments.length > 0 && (
              <CardContent className="space-y-2">
                {experiments.map((exp) => (
                  <Card key={exp.id} className="border shadow-none">
                    <div
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleExperiment(exp)}
                    >
                      {expandedExpId === exp.id ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{exp.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {exp.run_count} run{exp.run_count !== 1 ? "s" : ""} &middot;{" "}
                          {formatDate(exp.last_run_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {isDefined(exp.scores.f1) && (
                          <Badge
                            variant={
                              exp.scores.f1 >= 0.8
                                ? "success"
                                : exp.scores.f1 >= 0.5
                                  ? "warning"
                                  : "destructive"
                            }
                          >
                            F1: {pct(exp.scores.f1)}
                          </Badge>
                        )}
                        {isDefined(exp.scores.precision) && (
                          <span className="text-xs text-muted-foreground">
                            P: {pct(exp.scores.precision)}
                          </span>
                        )}
                        {isDefined(exp.scores.recall) && (
                          <span className="text-xs text-muted-foreground">
                            R: {pct(exp.scores.recall)}
                          </span>
                        )}
                        {exp.metadata?.tag && (
                          <Badge variant="outline" className="text-xs">
                            {exp.metadata.tag}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {expandedExpId === exp.id && (
                      <CardContent className="pt-0 border-t">
                        {loadingDetail ? (
                          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" /> Loading results...
                          </div>
                        ) : detail ? (
                          <div className="space-y-4 pt-4">
                            <div className="flex items-center gap-6 text-sm">
                              {isDefined(detail.aggregate_scores.f1) && (
                                <>
                                  <span>
                                    Avg F1: <strong>{pct(detail.aggregate_scores.f1)}</strong>
                                  </span>
                                  <span>
                                    Avg Precision:{" "}
                                    <strong>{pct(detail.aggregate_scores.precision)}</strong>
                                  </span>
                                  <span>
                                    Avg Recall:{" "}
                                    <strong>{pct(detail.aggregate_scores.recall)}</strong>
                                  </span>
                                </>
                              )}
                              {detail.examples.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs gap-1 ml-auto"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    exportExperimentResults(
                                      `${exp.name.replace(/\s+/g, "-")}_results.csv`,
                                      detail.examples,
                                    );
                                  }}
                                >
                                  <Download className="h-3.5 w-3.5" /> Export
                                </Button>
                              )}
                            </div>

                            {detail.examples.length > 0 && (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Example</TableHead>
                                    <TableHead className="w-[80px]">P</TableHead>
                                    <TableHead className="w-[80px]">R</TableHead>
                                    <TableHead className="w-[80px]">F1</TableHead>
                                    <TableHead className="w-[50px]">TP</TableHead>
                                    <TableHead className="w-[50px]">FP</TableHead>
                                    <TableHead className="w-[50px]">FN</TableHead>
                                    <TableHead className="w-[80px]">Files</TableHead>
                                    <TableHead className="w-[80px]">Latency</TableHead>
                                    <TableHead className="w-[30px]"></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {detail.examples.map((ex) => (
                                    <ExperimentExampleRow
                                      key={ex.run_id}
                                      example={ex}
                                      datasetId={selectedDatasetId || exp.dataset_id}
                                      pct={pct}
                                    />
                                  ))}
                                </TableBody>
                              </Table>
                            )}

                            {detail.examples.length === 0 && (
                              <p className="text-sm text-muted-foreground py-2">
                                No example results found for this experiment.
                              </p>
                            )}

                            {LANGSMITH_BASE && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <ExternalLink className="h-3 w-3" />
                                <a
                                  href={`${LANGSMITH_BASE}/datasets/${selectedDatasetId || exp.dataset_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline"
                                >
                                  View in LangSmith
                                </a>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </CardContent>
                    )}
                  </Card>
                ))}
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function BatchRunRow({ item }: { item: BatchRunItem }) {
  const [expanded, setExpanded] = useState(false);
  const pct = (v: number | null | undefined) =>
    isDefined(v) ? `${(v * 100).toFixed(1)}%` : "\u2014";
  const r = item.result;

  return (
    <>
      <TableRow
        className={`cursor-pointer hover:bg-muted/50 ${item.status === "failed" ? "bg-destructive/5" : ""}`}
        onClick={() => r && setExpanded(!expanded)}
      >
        <TableCell>
          {r ? (
            expanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )
          ) : null}
        </TableCell>
        <TableCell className="font-medium">{item.title}</TableCell>
        <TableCell>
          {item.status === "queued" && (
            <Badge variant="secondary" className="text-xs">
              Queued
            </Badge>
          )}
          {item.status === "running" && (
            <Badge variant="outline" className="text-xs">
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> Running
            </Badge>
          )}
          {item.status === "completed" && (
            <Badge variant="success" className="text-xs">
              Done
            </Badge>
          )}
          {item.status === "failed" && (
            <Badge variant="destructive" className="text-xs">
              Failed
            </Badge>
          )}
        </TableCell>
        <TableCell>{r ? pct(r.precision) : "\u2014"}</TableCell>
        <TableCell>{r ? pct(r.recall) : "\u2014"}</TableCell>
        <TableCell>
          {isDefined(r?.f1) ? (
            <Badge
              variant={r.f1 >= 0.8 ? "success" : r.f1 >= 0.5 ? "warning" : "destructive"}
              className="text-xs"
            >
              {pct(r.f1)}
            </Badge>
          ) : (
            "\u2014"
          )}
        </TableCell>
        <TableCell>{r ? r.selected_files.length : "\u2014"}</TableCell>
        <TableCell>
          {r
            ? `${r.latency_seconds}s`
            : item.status === "failed"
              ? item.error?.slice(0, 40)
              : "\u2014"}
        </TableCell>
      </TableRow>
      {expanded && r && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/30 p-4">
            <div className="space-y-3">
              {r.summary && <p className="text-sm text-muted-foreground">{r.summary}</p>}

              {isDefined(r.precision) && (
                <div className="flex items-center gap-6 text-sm">
                  <span>TP: {r.tp}</span>
                  <span>FP: {r.fp}</span>
                  <span>FN: {r.fn}</span>
                  {isDefined(r.total_tokens) && r.total_tokens > 0 && (
                    <span className="text-muted-foreground">
                      Tokens: {r.total_tokens.toLocaleString()}
                      {isDefined(r.prompt_tokens) && (
                        <span className="text-xs ml-1">
                          (in: {r.prompt_tokens.toLocaleString()}, out:{" "}
                          {(r.completion_tokens ?? 0).toLocaleString()})
                        </span>
                      )}
                    </span>
                  )}
                </div>
              )}

              {r.comparisons && r.comparisons.length > 0 ? (
                <div className="overflow-y-auto" style={{ maxHeight: "600px" }}>
                  <ComparisonTable
                    comparisons={r.comparisons}
                    exportName={`${item.title.replace(/\s+/g, "-")}_comparisons.csv`}
                  />
                </div>
              ) : (
                <>
                  {r.missing_files && r.missing_files.length > 0 && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                      <p className="text-xs font-semibold text-destructive mb-1">
                        Missing ({r.missing_files.length})
                      </p>
                      <div className="text-xs space-y-0.5">
                        {r.missing_files.map((f) => (
                          <p key={f} className="flex items-center gap-1">
                            <XCircle className="h-3 w-3" /> {f}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {r.unexpected_files && r.unexpected_files.length > 0 && (
                    <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
                      <p className="text-xs font-semibold text-warning mb-1">
                        Unexpected ({r.unexpected_files.length})
                      </p>
                      <div className="text-xs space-y-0.5">
                        {r.unexpected_files.map((f) => (
                          <p key={f} className="flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> {f}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {r.selected_files.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs font-semibold">
                          Selected Files ({r.selected_files.length})
                        </p>
                        <button
                          className="text-muted-foreground hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            exportSelectedFiles(
                              `${item.title.replace(/\s+/g, "-")}_selected.csv`,
                              r.selected_files,
                            );
                          }}
                          title="Export as CSV"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="overflow-y-auto max-h-[200px]">
                        <div className="text-xs space-y-0.5">
                          {r.selected_files.map((f) => (
                            <p
                              key={f.project_file_id}
                              className="flex items-center gap-2 text-muted-foreground"
                            >
                              <span className="font-mono">{f.file_name}</span>
                              <span className="text-muted-foreground/50">
                                ({Math.round(f.confidence * 100)}%)
                              </span>
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function RunEvalTab({
  datasets,
  loadingDatasets,
}: {
  datasets: DatasetInfo[];
  loadingDatasets: boolean;
}) {
  // Backend selector
  const [backendPreset, setBackendPreset] = useState<"prod" | "local" | "pr" | "custom">("prod");
  const [prNumber, setPrNumber] = useState("");
  const [customUrl, setCustomUrl] = useState("");

  const backendUrl = (() => {
    switch (backendPreset) {
      case "prod":
        return PROD_URL;
      case "local":
        return CLOUD_RUN_URL;
      case "pr":
        return prNumber ? PR_URL_TEMPLATE.replace("{N}", prNumber) : "";
      case "custom":
        return customUrl;
    }
  })();

  // Dataset picker
  const [selectedDatasetName, setSelectedDatasetName] = useState<string | null>(null);
  const [examples, setExamples] = useState<DatasetExample[]>([]);
  const [loadingExamples, setLoadingExamples] = useState(false);

  // Example selection
  const [selectedExampleIds, setSelectedExampleIds] = useState<Set<string>>(new Set());

  // Batch run state
  const [tag, setTag] = useState("");
  const [repeatCount, setRepeatCount] = useState(1);
  const [batchRuns, setBatchRuns] = useState<BatchRunItem[]>([]);
  const [dispatching, setDispatching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Validate-package state
  const [validatePackageId, setValidatePackageId] = useState("");
  const [validateExampleTitle, setValidateExampleTitle] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState<EvalResult | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const fetchExamples = async (name: string) => {
    setSelectedDatasetName(name);
    setLoadingExamples(true);
    try {
      const result = await evalFetch(
        `/api/eval/grouping/datasets/${encodeURIComponent(name)}/examples`,
      );
      setExamples(result);
      setSelectedExampleIds(new Set(result.map((ex: any) => ex.id)));
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingExamples(false);
    }
  };

  const isRunning = batchRuns.some((r) => r.status === "queued" || r.status === "running");

  const handleRunAll = async () => {
    if (!selectedDatasetName || !backendUrl) {
      toast.error("Select a dataset and backend first");
      return;
    }
    setDispatching(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Build list of runs: selected examples × repeat count
    const selectedExamples = examples.filter((ex: any) => selectedExampleIds.has(ex.id));
    const allRuns: BatchRunItem[] = [];
    let idx = 0;
    for (let i = 1; i <= repeatCount; i++) {
      for (const ex of selectedExamples) {
        allRuns.push({
          title: repeatCount > 1 ? `${ex.title} [${i}]` : ex.title,
          example_id: ex.id,
          runIndex: idx++,
          status: "queued",
        });
      }
    }
    setBatchRuns(allRuns);

    // Process runs with bounded concurrency to avoid saturating the backend
    const CONCURRENCY = 4;
    const completedPromises: Promise<any>[] = [];
    const runQueue = [...allRuns];

    async function processRun(run: BatchRunItem) {
      const i = run.runIndex;
      const ex = selectedExamples[i % selectedExamples.length];
      const iterTag =
        repeatCount > 1
          ? `${tag || "run"}-${Math.floor(i / selectedExamples.length) + 1}`
          : tag || undefined;

      setBatchRuns((cur) =>
        cur.map((r) => (r.runIndex === run.runIndex ? { ...r, status: "running" as const } : r)),
      );

      try {
        const result = await evalFetch(
          "/api/eval/grouping/run",
          {
            method: "POST",
            body: JSON.stringify({
              project_id: ex.project_id,
              title: ex.title,
              prompt: ex.prompt,
              procurement_form: ex.procurement_form,
              expected_files: ex.expected_files,
              dataset_name: selectedDatasetName,
              tag: iterTag,
            }),
            signal: controller.signal,
          },
          backendUrl,
        );

        setBatchRuns((cur) =>
          cur.map((r) =>
            r.runIndex === run.runIndex ? { ...r, status: "completed" as const, result } : r,
          ),
        );
        return result;
      } catch (err: any) {
        if (err.name === "AbortError") return null;
        setBatchRuns((cur) =>
          cur.map((r) =>
            r.runIndex === run.runIndex
              ? { ...r, status: "failed" as const, error: err.message }
              : r,
          ),
        );
        return null;
      }
    }

    // Simple semaphore: process at most CONCURRENCY runs at a time
    const executing = new Set<Promise<any>>();
    for (const run of runQueue) {
      const p = processRun(run).then((v) => {
        executing.delete(p);
        return v;
      });
      executing.add(p);
      completedPromises.push(p);
      if (executing.size >= CONCURRENCY) {
        await Promise.race(executing);
      }
    }

    const results = await Promise.allSettled(completedPromises);
    setDispatching(false);

    // Save completed results to LangSmith via the selected eval backend
    const completedResults = results
      .map((r) => (r.status === "fulfilled" ? r.value : null))
      .filter(Boolean);

    if (completedResults.length > 0) {
      evalFetch(
        "/api/eval/grouping/run/batch/save",
        {
          method: "POST",
          body: JSON.stringify({
            dataset_name: selectedDatasetName,
            tag: tag || undefined,
            results: completedResults,
          }),
        },
        backendUrl,
      )
        .then(() => toast.success(`Saved ${completedResults.length} results to LangSmith`))
        .catch((e: any) => toast.error(`Failed to save to LangSmith: ${e.message}`));
    }
  };

  // Aggregate stats from completed runs
  const completedRuns = batchRuns.filter((r) => r.status === "completed" && r.result);
  const totalTp = completedRuns.reduce((s, r) => s + (r.result?.tp ?? 0), 0);
  const totalFp = completedRuns.reduce((s, r) => s + (r.result?.fp ?? 0), 0);
  const totalFn = completedRuns.reduce((s, r) => s + (r.result?.fn ?? 0), 0);
  const overallPrecision = totalTp + totalFp > 0 ? totalTp / (totalTp + totalFp) : null;
  const overallRecall = totalTp + totalFn > 0 ? totalTp / (totalTp + totalFn) : null;
  const overallF1 =
    isDefined(overallPrecision) && isDefined(overallRecall) && overallPrecision + overallRecall > 0
      ? (2 * overallPrecision * overallRecall) / (overallPrecision + overallRecall)
      : null;
  const pct = (v: number | null) => (isDefined(v) ? `${(v * 100).toFixed(1)}%` : "\u2014");

  return (
    <div className="space-y-6">
      {/* Backend selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground">Backend:</span>
        {(["prod", "local", "pr", "custom"] as const).map((preset) => (
          <Button
            key={preset}
            size="sm"
            variant={backendPreset === preset ? "default" : "outline"}
            className="h-7 text-xs"
            onClick={() => setBackendPreset(preset)}
          >
            {preset === "prod"
              ? "Prod"
              : preset === "local"
                ? "Local"
                : preset === "pr"
                  ? "PR"
                  : "Custom"}
          </Button>
        ))}
        {backendPreset === "pr" && (
          <Input
            value={prNumber}
            onChange={(e) => setPrNumber(e.target.value.replace(/\D/g, ""))}
            placeholder="PR #"
            className="w-20 h-7 text-xs"
          />
        )}
        {backendPreset === "custom" && (
          <Input
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            placeholder="https://..."
            className="w-64 h-7 text-xs font-mono"
          />
        )}
        <span className="text-xs font-mono text-muted-foreground truncate max-w-[300px]">
          {backendUrl || "—"}
        </span>
      </div>

      {/* Dataset picker */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4" />
            Select Dataset
          </CardTitle>
          <CardDescription>
            Pick a dataset, then run the grouping agent against all examples.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loadingDatasets ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading datasets...
            </div>
          ) : datasets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No datasets found.</p>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {datasets.map((ds) => (
                <Button
                  key={ds.id}
                  variant={selectedDatasetName === ds.name ? "default" : "outline"}
                  size="sm"
                  onClick={() => fetchExamples(ds.name)}
                  disabled={loadingExamples || isRunning}
                >
                  {ds.name} ({ds.example_count})
                </Button>
              ))}
            </div>
          )}

          {loadingExamples && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading examples...
            </div>
          )}

          {!loadingExamples && selectedDatasetName && examples.length > 0 && (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30px]">
                        <input
                          type="checkbox"
                          checked={
                            selectedExampleIds.size === examples.length && examples.length > 0
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedExampleIds(new Set(examples.map((ex) => ex.id)));
                            } else {
                              setSelectedExampleIds(new Set());
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Ground Truth</TableHead>
                      <TableHead className="text-right">Expected Files</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {examples.map((ex) => (
                      <TableRow key={ex.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedExampleIds.has(ex.id)}
                            onChange={(e) => {
                              const next = new Set(selectedExampleIds);
                              if (e.target.checked) next.add(ex.id);
                              else next.delete(ex.id);
                              setSelectedExampleIds(next);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{ex.title}</TableCell>
                        <TableCell>
                          {ex.has_ground_truth ? (
                            <Badge variant="success">Yes</Badge>
                          ) : (
                            <Badge variant="secondary">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{ex.expected_file_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Run controls */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Tag</Label>
                  <Input
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    placeholder="e.g. experiment-v2"
                    className="w-48 h-8 text-xs"
                    disabled={isRunning}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Repeat</Label>
                  <Input
                    type="number"
                    value={repeatCount}
                    onChange={(e) =>
                      setRepeatCount(Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)))
                    }
                    className="w-16 h-8 text-xs"
                    min={1}
                    max={50}
                    disabled={isRunning}
                  />
                </div>
                <Button
                  onClick={handleRunAll}
                  disabled={dispatching || isRunning || selectedExampleIds.size === 0}
                >
                  {dispatching ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Dispatching...
                    </>
                  ) : isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Running {completedRuns.length}/{batchRuns.length}...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Run ({selectedExampleIds.size * repeatCount})
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Batch results */}
      {batchRuns.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Results — {selectedDatasetName}
                {tag && <span className="text-muted-foreground font-normal ml-2">({tag})</span>}
              </CardTitle>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">
                  {completedRuns.length}/{batchRuns.length} done
                </span>
                {completedRuns.length > 0 && isDefined(overallF1) && (
                  <Badge
                    variant={
                      overallF1 >= 0.8 ? "success" : overallF1 >= 0.5 ? "warning" : "destructive"
                    }
                  >
                    F1: {pct(overallF1)}
                  </Badge>
                )}
                {completedRuns.length > 0 && isDefined(overallPrecision) && (
                  <span className="text-xs text-muted-foreground">P: {pct(overallPrecision)}</span>
                )}
                {completedRuns.length > 0 && isDefined(overallRecall) && (
                  <span className="text-xs text-muted-foreground">R: {pct(overallRecall)}</span>
                )}
                {completedRuns.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() =>
                      exportBatchResults(
                        `${selectedDatasetName}${tag ? "_" + tag : ""}_results.csv`,
                        batchRuns,
                      )
                    }
                  >
                    <Download className="h-3.5 w-3.5" /> Export
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30px]"></TableHead>
                  <TableHead>Example</TableHead>
                  <TableHead className="w-[90px]">Status</TableHead>
                  <TableHead className="w-[70px]">P</TableHead>
                  <TableHead className="w-[70px]">R</TableHead>
                  <TableHead className="w-[70px]">F1</TableHead>
                  <TableHead className="w-[60px]">Files</TableHead>
                  <TableHead className="w-[80px]">Latency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchRuns.map((item) => (
                  <BatchRunRow key={item.runIndex} item={item} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Validate existing package */}
      {selectedDatasetName && examples.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Validate Existing Package</CardTitle>
            <CardDescription>
              Score an already-generated package against ground truth. Pick a package and example,
              then compare.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <input
                value={validatePackageId}
                onChange={(e) => setValidatePackageId(e.target.value.trim())}
                placeholder="Package ID"
                className="w-72 h-8 text-xs font-mono border rounded-md px-2 bg-background"
              />
              <select
                value={validateExampleTitle || ""}
                onChange={(e) => setValidateExampleTitle(e.target.value || null)}
                className="h-8 text-xs border rounded-md px-2 bg-background"
              >
                <option value="">Example...</option>
                {examples
                  .filter((ex: any) => ex.has_ground_truth)
                  .map((ex: any) => (
                    <option key={ex.id} value={ex.title}>
                      {ex.title}
                    </option>
                  ))}
              </select>
              <Button
                size="sm"
                onClick={async () => {
                  if (!validatePackageId || !validateExampleTitle || !selectedDatasetName) return;
                  setValidating(true);
                  setValidateResult(null);
                  try {
                    const result = await evalFetch("/api/eval/grouping/validate-package", {
                      method: "POST",
                      body: JSON.stringify({
                        package_id: validatePackageId,
                        dataset_name: selectedDatasetName,
                        example_title: validateExampleTitle,
                        save_to_langsmith: true,
                        tag: tag || undefined,
                      }),
                    });
                    setValidateResult(result);
                    const f1Str = isDefined(result.f1) ? (result.f1 * 100).toFixed(1) + "%" : "N/A";
                    toast.success(`F1 = ${f1Str} — saved to LangSmith`);
                  } catch (err: any) {
                    toast.error(err.message);
                  } finally {
                    setValidating(false);
                  }
                }}
                disabled={!validatePackageId || !validateExampleTitle || validating}
              >
                {validating ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-1" /> Validating...
                  </>
                ) : (
                  "Validate"
                )}
              </Button>
            </div>

            {validateResult && (
              <div className="space-y-3 pt-3 border-t">
                <div className="flex items-center gap-6 text-sm flex-wrap">
                  <Badge
                    variant={
                      isDefined(validateResult.f1) && validateResult.f1 >= 0.8
                        ? "success"
                        : isDefined(validateResult.f1) && validateResult.f1 >= 0.5
                          ? "warning"
                          : "destructive"
                    }
                  >
                    F1: {isDefined(validateResult.f1) ? pct(validateResult.f1) : "\u2014"}
                  </Badge>
                  <span>
                    P:{" "}
                    {isDefined(validateResult.precision) ? pct(validateResult.precision) : "\u2014"}
                  </span>
                  <span>
                    R: {isDefined(validateResult.recall) ? pct(validateResult.recall) : "\u2014"}
                  </span>
                  <span className="text-muted-foreground">
                    TP: {validateResult.tp} FP: {validateResult.fp} FN: {validateResult.fn}
                  </span>
                  <span className="text-muted-foreground">
                    {validateResult.selected_files.length} files in package
                  </span>
                </div>
                {validateResult.comparisons && validateResult.comparisons.length > 0 && (
                  <div className="overflow-y-auto" style={{ maxHeight: "600px" }}>
                    <ComparisonTable comparisons={validateResult.comparisons} />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ExperimentExampleRow({
  example,
  datasetId,
  pct,
}: {
  example: ExperimentExampleResult;
  datasetId: string;
  pct: (v: number | null | undefined) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const runUrl = example.run_id
    ? `${LANGSMITH_BASE}/datasets/${datasetId}/runs/${example.run_id}`
    : `${LANGSMITH_BASE}/datasets/${datasetId}`;

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setExpanded(!expanded)}>
        <TableCell className="font-medium">
          <div className="flex items-center gap-1">
            {expanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
            {example.title}
          </div>
        </TableCell>
        <TableCell>{pct(example.scores.precision)}</TableCell>
        <TableCell>{pct(example.scores.recall)}</TableCell>
        <TableCell>
          {isDefined(example.scores.f1) ? (
            <Badge
              variant={
                example.scores.f1 >= 0.8
                  ? "success"
                  : example.scores.f1 >= 0.5
                    ? "warning"
                    : "destructive"
              }
              className="text-xs"
            >
              {pct(example.scores.f1)}
            </Badge>
          ) : (
            "\u2014"
          )}
        </TableCell>
        <TableCell>{example.scores.tp ?? "\u2014"}</TableCell>
        <TableCell>{example.scores.fp ?? "\u2014"}</TableCell>
        <TableCell>{example.scores.fn ?? "\u2014"}</TableCell>
        <TableCell>{example.selected_file_count}</TableCell>
        <TableCell>
          {isDefined(example.latency_seconds) ? `${example.latency_seconds.toFixed(1)}s` : "\u2014"}
        </TableCell>
        <TableCell>
          {LANGSMITH_BASE && (
            <a
              href={runUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground"
              onClick={(e) => e.stopPropagation()}
              title="View run in LangSmith"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded detail row */}
      {expanded && (
        <TableRow>
          <TableCell colSpan={10} className="bg-muted/30 p-4">
            <div className="space-y-3">
              {example.summary && (
                <p className="text-sm text-muted-foreground">{example.summary}</p>
              )}

              {isDefined(example.scores.total_tokens) && example.scores.total_tokens > 0 && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Tokens: {Math.round(example.scores.total_tokens).toLocaleString()}</span>
                  {isDefined(example.scores.prompt_tokens) && (
                    <span>
                      (in: {Math.round(example.scores.prompt_tokens).toLocaleString()}, out:{" "}
                      {Math.round(example.scores.completion_tokens ?? 0).toLocaleString()})
                    </span>
                  )}
                </div>
              )}

              {/* Structured comparison table (preferred) or fallback to plain text */}
              {example.comparisons && example.comparisons.length > 0 ? (
                <div className="overflow-y-auto" style={{ maxHeight: "600px" }}>
                  <ComparisonTable
                    comparisons={example.comparisons}
                    exportName={`${example.title.replace(/\s+/g, "-")}_comparisons.csv`}
                  />
                </div>
              ) : (
                <>
                  {example.missing_files && example.missing_files.length > 0 && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                      <p className="text-xs font-semibold text-destructive mb-1">
                        Missing ({example.missing_files.length})
                      </p>
                      <div className="text-xs space-y-0.5">
                        {example.missing_files.map((f) => (
                          <p key={f} className="flex items-center gap-1">
                            <XCircle className="h-3 w-3" /> {f}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {example.unexpected_files && example.unexpected_files.length > 0 && (
                    <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
                      <p className="text-xs font-semibold text-warning mb-1">
                        Unexpected ({example.unexpected_files.length})
                      </p>
                      <div className="text-xs space-y-0.5">
                        {example.unexpected_files.map((f) => (
                          <p key={f} className="flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> {f}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {example.selected_files.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs font-semibold">
                          Selected Files ({example.selected_files.length})
                        </p>
                        <button
                          className="text-muted-foreground hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            exportSelectedFiles(
                              `${example.title.replace(/\s+/g, "-")}_selected.csv`,
                              example.selected_files,
                            );
                          }}
                          title="Export as CSV"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="overflow-y-auto max-h-[200px]">
                        <div className="text-xs space-y-0.5">
                          {example.selected_files.map((f) => (
                            <p
                              key={f.project_file_id}
                              className="flex items-center gap-2 text-muted-foreground"
                            >
                              <span className="font-mono">{f.file_name}</span>
                              <span className="text-muted-foreground/50">
                                ({Math.round(f.confidence * 100)}%)
                              </span>
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function GroupingEvalPage() {
  const { isAdmin, isLoading: adminLoading } = useAdminAccess();

  // Shared datasets state — fetched once, passed to both tabs
  const [datasets, setDatasets] = useState<DatasetInfo[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);

  const fetchDatasets = async () => {
    setLoadingDatasets(true);
    try {
      const result = await evalFetch("/api/eval/grouping/datasets");
      setDatasets(result);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingDatasets(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchDatasets();
  }, [isAdmin]);

  if (adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Access denied. Admin only.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="bg-background p-8 pb-16 h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="h-6 w-6" />
            Grouping Agent Eval
          </h1>
          <p className="text-muted-foreground mt-1">
            Create datasets, verify ground truth, and run the production grouping agent.
          </p>
        </div>

        <Tabs defaultValue="run" className="space-y-4">
          <TabsList>
            <TabsTrigger value="run">Run Eval</TabsTrigger>
            <TabsTrigger value="datasets">Datasets</TabsTrigger>
            <TabsTrigger value="create">Create Dataset</TabsTrigger>
          </TabsList>

          <TabsContent value="run">
            <RunEvalTab datasets={datasets} loadingDatasets={loadingDatasets} />
          </TabsContent>

          <TabsContent value="datasets">
            <DatasetsTab datasets={datasets} loading={loadingDatasets} />
          </TabsContent>

          <TabsContent value="create">
            <CreateDatasetTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
